"use client";

import { createPortal } from "react-dom";
import { useTaskContext } from "@/contexts/TaskContext";
import { useIsDesktop } from "@/hooks/useIsDesktop";
import type { DemoAgent } from "@/lib/app-types";

import WelcomeState from "./WelcomeState";
import ThinkingIndicator from "./ThinkingIndicator";
import MatchingIndicator from "./MatchingIndicator";
import AgentCarousel from "./AgentCarousel";
import WorkingProgress from "./WorkingProgress";
import ResultCard from "./ResultCard";
import RatingModal from "./RatingModal";
import type { AgentCardData } from "./AgentCard";

interface PhaseViewProps {
  onSubmit: (text: string) => void;
  onBrowseAll: () => void;
  onAgentCardClick?: (agent: DemoAgent) => void;
}

function toCardData(agent: DemoAgent, isBestMatch: boolean): AgentCardData {
  return {
    id: agent.id,
    name: agent.name,
    avatarInitials: agent.avatarInitials,
    rating: agent.rating,
    tasksCompleted: agent.tasksCompleted,
    tags: agent.tags,
    status: agent.status,
    price: agent.basePrice,
    isBestMatch,
  };
}

export default function PhaseView({ onSubmit, onBrowseAll, onAgentCardClick }: PhaseViewProps) {
  const ctx = useTaskContext();
  const isDesktop = useIsDesktop();
  const { phase, task } = ctx;

  if (phase === "idle") return <WelcomeState onChipClick={onSubmit} />;

  if (phase === "submitted") return <ThinkingIndicator />;

  // clarifying + budget + tier + format phases: cards render in the bottom
  // input-bar slot (in /app/page.tsx), not inline in the canvas.
  if (phase === "clarifying") return null;
  if (phase === "budget") return null;
  if (phase === "tier") return null;
  if (phase === "format") return null;

  if (phase === "matching") return <MatchingIndicator />;

  if (phase === "selecting" && task) {
    const cards = task.topAgents.map((a, i) => toCardData(a, i === 0));
    const agentsById = new Map(task.topAgents.map((a) => [a.id, a]));
    return (
      <AgentCarousel
        agents={cards}
        onSelect={(id) => {
          const agent = agentsById.get(id);
          if (!agent) return;
          if (onAgentCardClick) onAgentCardClick(agent);
          else ctx.dispatch({ type: "SELECT_AGENT", agent });
        }}
        onBrowseAll={onBrowseAll}
      />
    );
  }

  if ((phase === "confirmed" || phase === "working") && task && task.selectedAgent) {
    return (
      <WorkingProgress
        agent={task.selectedAgent}
        progress={ctx.workingProgress}
        status={ctx.workingStatus}
      />
    );
  }

  if (phase === "delivered" && task && task.result) {
    return (
      <ResultCard
        title={task.result.title}
        summary={task.result.summary}
        competitors={task.result.competitors}
        recommendations={task.result.recommendations}
        files={task.result.files}
        taskId={ctx.currentTaskId}
        result={task.result}
        onApprove={() => {
          // Goes through the mode-aware approval path — demo just
          // advances the reducer, live waits for the WS approve +
          // toasts tx progress.
          ctx.approveCurrentTask(task.rating ?? 0).catch(() => {});
        }}
        onRevise={() =>
          task.revisionCount < 3
            ? ctx.dispatch({ type: "REQUEST_REVISION" })
            : undefined
        }
      />
    );
  }

  if (phase === "approved" || phase === "rating") {
    const modal = (
      <div className="app-modal-backdrop" role="dialog" aria-modal="true">
        <RatingModal
          onSubmit={(rating, feedback) => ctx.dispatch({ type: "RATE", rating, feedback })}
          onSkip={() => ctx.dispatch({ type: "SKIP_RATING" })}
        />
      </div>
    );
    // Desktop: portal the modal to <body> so the fixed backdrop
    // escapes the chat canvas's overflow/layout context and covers
    // the entire viewport — sidebar included. Web keeps the inline
    // render (the chat canvas is already full-bleed on web).
    if (isDesktop && typeof document !== "undefined") {
      return createPortal(modal, document.body);
    }
    return modal;
  }

  // 'completed' phase renders CompletedBanner in the footer region of /app/page.tsx,
  // not here in the canvas.

  return null;
}
